import { useSiteStore } from "../../stores/useSiteStore";
import { assetColor, assetInfo, siteTypeStyle, SITE_TYPE_STYLES } from "../../constants/siteConstants";
import { Search, X, MapPin, ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";

// ---------------------------------------------------------------------------
// Asset type taxonomy — maps asset_type to display label + icon char
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// Helpers — assetInfo, assetColor, siteTypeStyle imported from siteConstants
// ---------------------------------------------------------------------------

const ASSET_COLORS = {
  anchor:    { bg: "bg-teal-500/15",   border: "border-teal-500/30",   text: "text-teal-300"   },
  component: { bg: "bg-blue-500/15",   border: "border-blue-500/30",   text: "text-blue-300"   },
  context:   { bg: "bg-slate-500/15",  border: "border-slate-500/30",  text: "text-slate-400"  },
};

function confidenceBar(value) {
  if (value == null) return null;
  const pct = Math.round(value * 100);
  const color = pct >= 80 ? "#22c55e" : pct >= 50 ? "#f59e0b" : "#ef4444";
  return { pct, color };
}

function groupFeatures(features) {
  const groups = { anchor: [], component: [], context: [] };
  for (const f of features) {
    const info = assetInfo(f.properties?.feature_class);
    groups[info.group].push(f);
  }
  return groups;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-4 px-6 text-center">
      <div className="w-12 h-12 rounded-full bg-slate-800/80 border border-slate-700/50
                      flex items-center justify-center">
        <MapPin size={16} className="text-slate-500" />
      </div>
      <p className="text-[11px] text-slate-500 leading-relaxed max-w-[180px]">
        Click a site on the map to inspect its features and context.
      </p>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="p-4 space-y-4 animate-pulse">
      <div className="space-y-2">
        <div className="h-4 bg-slate-800 rounded w-3/4" />
        <div className="h-3 bg-slate-800 rounded w-1/2" />
      </div>
      <div className="flex gap-2">
        <div className="h-5 bg-slate-800 rounded-full w-20" />
        <div className="h-5 bg-slate-800 rounded-full w-16" />
      </div>
      <div className="h-px bg-slate-800" />
      {[...Array(5)].map((_, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="h-5 w-5 bg-slate-800 rounded" />
          <div className="h-3 bg-slate-800 rounded flex-1" />
          <div className="h-3 bg-slate-800 rounded w-8" />
        </div>
      ))}
    </div>
  );
}

function SiteHeader({ detail }) {
  const conf  = confidenceBar(detail.confidence);
  const style = siteTypeStyle(detail.site_type);

  return (
    <div className="p-4 border-b border-slate-800/80">

      {/* Site type pill + name */}
      <div className="flex items-start gap-2.5 mb-3">
        <div
          className="mt-0.5 w-2.5 h-2.5 rounded-full shrink-0 ring-2 ring-offset-1 ring-offset-slate-900"
          style={{ backgroundColor: style.color, ringColor: style.color + "40" }}
        />
        <div className="min-w-0">
          <div className="text-[11px] font-semibold tracking-wide font-mono uppercase"
               style={{ color: style.color }}>
            {style.label}
          </div>
          <div className="text-[13px] font-medium text-white leading-snug mt-0.5 truncate">
            {detail.site_name || `Site ${detail.site_key?.slice(5, 13)}`}
          </div>
        </div>
      </div>

      {/* Derivation + key */}
      <div className="flex items-center gap-2 mb-3">
        {detail.derivation_method && (
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-800
                           text-slate-400 border border-slate-700/50 font-mono uppercase tracking-wide">
            {detail.derivation_method.replace(/_/g, " ")}
          </span>
        )}
        <span className="text-[9px] text-slate-600 font-mono truncate">
          {detail.site_key?.slice(0, 24)}…
        </span>
      </div>

      {/* Confidence */}
      {conf && (
        <div className="mb-3">
          <div className="flex justify-between text-[10px] mb-1">
            <span className="text-slate-500">Confidence</span>
            <span className="font-medium" style={{ color: conf.color }}>{conf.pct}%</span>
          </div>
          <div className="h-1 bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500"
                 style={{ width: `${conf.pct}%`, backgroundColor: conf.color }} />
          </div>
        </div>
      )}

      {/* Feature count */}
      <div className="text-[10px] text-slate-500">
        {detail.feature_count ?? 0} associated features
      </div>

    </div>
  );
}

function ContextSection({ detail }) {
  const counts = [
    { label: "Tanks",        value: detail.tank_count       },
    { label: "Roads",        value: detail.road_count       },
    { label: "OG Leases",    value: detail.og_lease_count   },
    { label: "SR Leases",    value: detail.sr_lease_count   },
    { label: "Easements",    value: detail.sr_easement_count},
    { label: "Survey Marks", value: detail.survey_mark_count},
    { label: "Gate Access",  value: detail.fence_access_count},
  ].filter(c => c.value != null && c.value > 0);

  const hasBadges = detail.has_fence || detail.has_tank_battery;
  if (!hasBadges && counts.length === 0) return null;

  return (
    <div className="px-4 py-3 border-b border-slate-800/80">
      <div className="text-[9px] font-mono uppercase tracking-widest text-slate-600 mb-2.5">
        Site Context
      </div>

      {hasBadges && (
        <div className="flex gap-1.5 mb-2.5 flex-wrap">
          {detail.has_fence && (
            <span className="text-[10px] px-2 py-0.5 rounded-full
                             bg-amber-500/10 border border-amber-500/20 text-amber-400">
              ⬟ Fenced
            </span>
          )}
          {detail.has_tank_battery && (
            <span className="text-[10px] px-2 py-0.5 rounded-full
                             bg-blue-500/10 border border-blue-500/20 text-blue-400">
              ⊙ Tank Battery
            </span>
          )}
        </div>
      )}

      {counts.length > 0 && (
        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
          {counts.map(({ label, value }) => (
            <div key={label}
                 className="flex justify-between items-baseline py-0.5
                            border-b border-slate-800/40 last:border-0">
              <span className="text-[10px] text-slate-500">{label}</span>
              <span className="text-[10px] font-medium text-slate-300">{value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FeatureGroup({ title, features, colorSet, defaultOpen = true, onFocus }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!features.length) return null;

  return (
    <div className="border-b border-slate-800/60 last:border-0">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-2
                   hover:bg-slate-800/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className={`text-[9px] px-1.5 py-0.5 rounded border font-medium
                            ${colorSet.bg} ${colorSet.border} ${colorSet.text}`}>
            {title}
          </span>
          <span className="text-[10px] text-slate-500">{features.length}</span>
        </div>
        {open
          ? <ChevronDown size={11} className="text-slate-600" />
          : <ChevronRight size={11} className="text-slate-600" />
        }
      </button>

      {open && (
        <div className="px-4 pb-2 space-y-0.5">
          {features.map((f, i) => {
            const info = assetInfo(f.properties?.feature_class);
            const dist = f.properties?.distance_m;
            return (
              <div key={f.id ?? i}
                   onClick={() => onFocus?.(f.id)}
                   className="flex items-center gap-2 py-1 rounded
                              hover:bg-slate-800/40 active:bg-slate-700/60
                              transition-colors px-1 cursor-pointer">
                <span
                  className="text-[11px] w-4 text-center shrink-0 font-bold"
                  style={{ color: assetColor(f.properties?.feature_class) }}
                >
                  {info.icon}
                </span>
                <span className="text-[11px] text-slate-300 truncate flex-1">
                  {info.label}
                </span>
                {dist != null && (
                  <span className="text-[10px] text-slate-600 shrink-0">
                    {Math.round(dist)}m
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FeaturesSection({ features, query, setQuery, onFocusFeature }) {
  if (!features) return null;
  const items = features.features ?? [];

  const filtered = query.length > 1
    ? items.filter(f =>
        assetInfo(f.properties?.feature_class).label
          .toLowerCase().includes(query.toLowerCase()) ||
        f.properties?.membership_role?.toLowerCase().includes(query.toLowerCase())
      )
    : items;

  const groups = groupFeatures(filtered);

  return (
    <div>
      <div className="px-4 pt-3 pb-2 flex items-center justify-between">
        <div className="text-[9px] font-mono uppercase tracking-widest text-slate-600">
          Features
        </div>
        <span className="text-[10px] text-slate-500">
          {filtered.length}{filtered.length !== items.length && `/${items.length}`}
        </span>
      </div>

      {items.length > 6 && (
        <div className="relative px-4 mb-2">
          <Search size={10} className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-600" />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Filter…"
            className="w-full pl-6 pr-6 py-1.5 text-[11px] bg-slate-800/60
                       border border-slate-700/50 rounded text-slate-300
                       placeholder:text-slate-600 outline-none
                       focus:border-teal-500/40 transition-colors"
          />
          {query && (
            <button onClick={() => setQuery("")}
                    className="absolute right-6 top-1/2 -translate-y-1/2 text-slate-600
                               hover:text-slate-400 transition-colors">
              <X size={10} />
            </button>
          )}
        </div>
      )}

      <FeatureGroup
        title="ANCHOR"
        features={groups.anchor}
        colorSet={ASSET_COLORS.anchor}
        defaultOpen={true}
        onFocus={onFocusFeature}
      />
      <FeatureGroup
        title="COMPONENT"
        features={groups.component}
        colorSet={ASSET_COLORS.component}
        defaultOpen={true}
        onFocus={onFocusFeature}
      />
      <FeatureGroup
        title="CONTEXT"
        features={groups.context}
        colorSet={ASSET_COLORS.context}
        defaultOpen={false}
        onFocus={onFocusFeature}
      />

      {filtered.length === 0 && (
        <div className="px-4 py-3 text-[11px] text-slate-600">
          No features match
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------

export default function SiteIDPanel() {
  const selectedSiteId      = useSiteStore(s => s.selectedSiteId);
  const selectedSiteDetail  = useSiteStore(s => s.selectedSiteDetail);
  const siteFeatures        = useSiteStore(s => s.siteFeatures);
  const loadingDetail       = useSiteStore(s => s.loadingDetail);
  const detailError         = useSiteStore(s => s.detailError);
  const clearSelection      = useSiteStore(s => s.clearSelection);
  const focusFeature        = useSiteStore(s => s.focusFeature);
  const viewport            = useSiteStore(s => s.viewport);
  const loadingViewport     = useSiteStore(s => s.loadingViewport);
  const siteCollection      = useSiteStore(s => s.siteCollection);

  const [featureQuery, setFeatureQuery] = useState("");

  const siteCount = siteCollection.features?.length ?? 0;

  return (
    <div className="h-full w-full bg-slate-900 border-r border-slate-800/80 flex flex-col">

      {/* ── Header ── */}
      <div className="p-3 border-b border-slate-800 flex items-center justify-between shrink-0">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-gray-400 flex items-center gap-2">
            <MapPin size={12} />
            AssetID
          </div>
          <div className="text-[10px] text-gray-500 mt-0.5">
            {loadingViewport
              ? "Loading…"
              : `${siteCount} site${siteCount !== 1 ? "s" : ""} in view`}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {loadingViewport && (
            <div className="w-2 h-2 rounded-full bg-status-busy animate-pulse" />
          )}
          {selectedSiteId && (
            <button
              onClick={() => { clearSelection(); setFeatureQuery(""); }}
              className="text-[10px] text-slate-500 hover:text-slate-300
                         flex items-center gap-1 transition-colors"
            >
              <X size={11} /> Clear
            </button>
          )}
        </div>
      </div>

      {/* ── Body ── */}
      <div className="flex-1 overflow-y-auto min-h-0">

        {!selectedSiteId && !loadingDetail && <EmptyState />}

        {selectedSiteId && loadingDetail && <Skeleton />}

        {selectedSiteId && detailError && !loadingDetail && (
          <div className="p-4 text-[11px] text-red-400/80">
            Failed to load: {detailError}
          </div>
        )}

        {selectedSiteDetail && !loadingDetail && (
          <>
            <SiteHeader detail={selectedSiteDetail} />
            <ContextSection detail={selectedSiteDetail} />
            <FeaturesSection
              features={siteFeatures}
              query={featureQuery}
              setQuery={setFeatureQuery}
              onFocusFeature={focusFeature}
            />
          </>
        )}

      </div>

      {/* ── Footer ── */}
      {!selectedSiteId && viewport?.zoom < 13 && (
        <div className="px-4 py-2 border-t border-slate-800/60
                        text-[10px] text-slate-600 text-center shrink-0">
          Zoom in to see individual sites
        </div>
      )}

    </div>
  );
}
